/* Spot Trading Management — pixel-matched to CoinZii. 7 tabs, every tile,
   filter, table column, badge, and action wired to real backend endpoints. */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, AlertTriangle, BarChart3, CheckCircle2, ChevronDown, Download,
  Edit2, Eye, ExternalLink, FileText, Globe2, ListOrdered, Plus,
  RefreshCw, Search, Settings, Shield, ShoppingCart, TrendingUp, X, Zap,
  Lock, XCircle,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, apiBaseUrl } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

// ----- shared mini primitives (kept local so visuals match exactly) -----
const C = {
  bg: "#0A0F1F", surface: "#0F1626", border: "#1E2845", text: "#E5E9F2",
  muted: "#8892A8", dim: "#5A6582", gold: "#F0B83A", green: "#10B981",
  red: "#EF4444", blue: "#3B82F6", purple: "#8B5CF6", amber: "#F59E0B",
};

function Card({ children, className = "", style }) {
  return (
    <div className={`rounded-[14px] border bg-[${C.surface}] ${className}`}
      style={{ borderColor: C.border, ...style }}>{children}</div>
  );
}

function GradKpi({ label, value, sub, icon: Icon, tint, testid }) {
  // tint: green | yellow | blue | red | purple | cyan
  const tints = {
    green:  ["rgba(16,185,129,0.55)",  "rgba(16,185,129,0.10)",  C.green],
    yellow: ["rgba(240,184,58,0.55)",  "rgba(240,184,58,0.10)",  C.gold],
    blue:   ["rgba(59,130,246,0.55)",  "rgba(59,130,246,0.10)",  C.blue],
    red:    ["rgba(239,68,68,0.55)",   "rgba(239,68,68,0.10)",   C.red],
    purple: ["rgba(139,92,246,0.55)",  "rgba(139,92,246,0.10)",  C.purple],
    cyan:   ["rgba(6,182,212,0.55)",   "rgba(6,182,212,0.10)",   "#06B6D4"],
  }[tint] || ["rgba(240,184,58,0.55)", "rgba(240,184,58,0.10)", C.gold];
  const [border, glow, accent] = tints;
  return (
    <div data-testid={testid}
      className="relative rounded-[14px] p-5 overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${glow} 0%, transparent 60%), ${C.surface}`, border: `1px solid ${border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[12px]" style={{ color: C.muted }}>{label}</div>
          <div className="text-[28px] font-bold mt-1.5 tabular-nums leading-tight break-all">{value}</div>
          {sub && <div className="mt-1.5 text-[11px]">{sub}</div>}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-[10px] grid place-items-center"
            style={{ background: `${accent}22`, color: accent }}>
            <Icon size={16} />
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ value, kind = "auto" }) {
  const map = {
    active: [C.green, "rgba(16,185,129,0.15)"],
    enabled: [C.green, "rgba(16,185,129,0.15)"],
    settled: [C.green, "rgba(16,185,129,0.15)"],
    resolved: [C.green, "rgba(16,185,129,0.15)"],
    yes: [C.green, "rgba(16,185,129,0.15)"],
    open: [C.amber, "rgba(245,158,11,0.18)"],
    pending: [C.amber, "rgba(245,158,11,0.18)"],
    medium: [C.amber, "rgba(245,158,11,0.18)"],
    halted: [C.red, "rgba(239,68,68,0.18)"],
    no: [C.red, "rgba(239,68,68,0.18)"],
    high: [C.red, "rgba(239,68,68,0.18)"],
    critical: [C.red, "rgba(239,68,68,0.18)"],
    cancelled: [C.dim, "rgba(90,101,130,0.25)"],
    closed: [C.dim, "rgba(90,101,130,0.25)"],
    low: [C.green, "rgba(16,185,129,0.15)"],
    buy: [C.green, "rgba(16,185,129,0.15)"],
    sell: [C.red, "rgba(239,68,68,0.18)"],
  };
  const [fg, bg] = map[String(value || "").toLowerCase()] || [C.muted, "rgba(136,146,168,0.15)"];
  return <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md"
    style={{ color: fg, background: bg }}>{value}</span>;
}

function PrimaryTabs({ tabs, value, onChange }) {
  return (
    <div className="rounded-[14px] border bg-[#0F1626] p-1 flex gap-1 overflow-x-auto"
      style={{ borderColor: C.border }}>
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button key={t.id} onClick={() => onChange(t.id)}
            data-testid={`spot-tab-${t.id}`}
            className="relative flex items-center gap-2 px-4 h-[40px] rounded-[10px] text-[13px] font-bold whitespace-nowrap transition"
            style={active
              ? { background: C.gold, color: C.bg }
              : { color: C.muted, background: "transparent" }}>
            <t.icon size={14} />{t.label}
          </button>
        );
      })}
    </div>
  );
}

function PillTabs({ tabs, value, onChange, testidPrefix = "subtab" }) {
  return (
    <div className="flex gap-1.5">
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button key={t.id} onClick={() => onChange(t.id)}
            data-testid={`${testidPrefix}-${t.id}`}
            className="px-3.5 h-[34px] rounded-[8px] text-[12px] font-semibold whitespace-nowrap transition"
            style={active
              ? { background: C.gold, color: C.bg }
              : { color: C.muted, background: "transparent" }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function GhostBtn({ children, onClick, color = C.muted, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 h-[36px] px-3 rounded-[8px] text-[12px] font-bold border transition hover:opacity-90"
      style={{ borderColor: `${color}55`, color, background: `${color}10` }}>
      {children}
    </button>
  );
}

function fmtINR(v) {
  if (!Number.isFinite(v)) return "—";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN")}`;
}
function fmtUSD(v, max = 2) { return `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: max })}`; }
function fmtVol(v) {
  const n = Number(v || 0);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

const TABS = [
  { id: "markets",   label: "Markets",          icon: BarChart3 },
  { id: "orderbook", label: "Order Book (LIVE)", icon: Activity },
  { id: "orders",    label: "Orders",            icon: FileText },
  { id: "trades",    label: "Trades",            icon: TrendingUp },
  { id: "liquidity", label: "Liquidity",         icon: ShoppingCart },
  { id: "risk",      label: "Risk & Alerts",     icon: AlertTriangle },
  { id: "audit",     label: "Audit Logs",        icon: Shield },
];

// ============================================================================
export default function SpotTrading() {
  const [tab, setTab] = useState("markets");
  const [kpis, setKpis] = useState({ active_pairs: 0, volume_24h_inr: 0, volume_24h_delta_pct: 0, open_orders: 0, risk_alerts: 0 });

  useEffect(() => {
    const load = () => api.get("/api/admin/spot/kpis").then(({ data }) => setKpis(data)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [tab]);

  return (
    <AdminShell title="Spot Trading Management">
      <div className="space-y-5 pb-8">
        {/* TOP KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <GradKpi tint="green" label="Active Pairs" value={kpis.active_pairs} icon={BarChart3} testid="kpi-active-pairs" />
          <GradKpi tint="yellow" label="24h Volume" value={fmtINR(kpis.volume_24h_inr)} icon={TrendingUp}
            sub={<span className="inline-flex items-center gap-1 font-semibold" style={{ color: C.green }}>
              <TrendingUp size={11} /> +{kpis.volume_24h_delta_pct}%
            </span>} testid="kpi-24h-volume" />
          <GradKpi tint="blue" label="Open Orders" value={kpis.open_orders.toLocaleString()} icon={FileText} testid="kpi-open-orders" />
          <GradKpi tint="red" label="Risk Alerts" value={kpis.risk_alerts} icon={AlertTriangle} testid="kpi-risk-alerts" />
        </div>

        {/* TABS */}
        <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} />

        <div>
          {tab === "markets" && <MarketsTab />}
          {tab === "orderbook" && <OrderBookTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "trades" && <TradesTab />}
          {tab === "liquidity" && <LiquidityTab />}
          {tab === "risk" && <RiskTab />}
          {tab === "audit" && <AuditTab />}
        </div>
      </div>
    </AdminShell>
  );
}

// ============================================================================
// MARKETS
// ============================================================================
function MarketsTab() {
  const [sub, setSub] = useState("all");
  const [pairs, setPairs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [health, setHealth] = useState([]);
  const pager = useAdminPager();

  const reload = () =>
    api.get("/api/admin/spot/pairs").then(({ data }) => setPairs(asAdminList(data))).catch(() => setPairs([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (sub === "health") api.get("/api/admin/spot/market-health").then(({ data }) => setHealth(asAdminList(data)));
  }, [sub]);

  const filtered = asAdminList(pairs).filter((p) =>
    (!q || p.pair.toLowerCase().includes(q.toLowerCase())) &&
    (!statusFilter || p.status === statusFilter)
  );

  useEffect(() => { pager.resetPage(); }, [q, statusFilter, sub, pager.resetPage]);
  useEffect(() => { pager.setTotal(filtered.length); }, [filtered.length, pager.setTotal]);

  const pageRows = filtered.slice(pager.skip, pager.skip + pager.pageSize);

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <PillTabs tabs={[{ id: "all", label: "All Pairs" }, { id: "health", label: "Market Health" }, { id: "fees", label: "Fees & Commission" }]} value={sub} onChange={setSub} testidPrefix="mkt-sub" />
      </div>

      {sub === "all" && (
        <>
          <div className="border-t pt-5 flex flex-wrap items-center gap-3" style={{ borderColor: C.border }}>
            <div className="flex items-center gap-2 px-3 h-[42px] rounded-[10px] border bg-[#0A0F1F] flex-1 min-w-[260px]"
              style={{ borderColor: C.border }}>
              <Search size={14} style={{ color: C.dim }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pairs..."
                data-testid="market-search"
                className="bg-transparent outline-none text-[13px] flex-1" />
            </div>
            <div className="relative">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="market-status-filter"
                className="appearance-none h-[42px] pl-3 pr-9 rounded-[10px] border bg-[#0A0F1F] text-[13px]"
                style={{ borderColor: C.border, color: C.text }}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="halted">Halted</option>
                <option value="maintenance">Maintenance</option>
              </select>
              <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.dim }} />
            </div>
            <div className="ml-auto" />
            <button onClick={() => setEditing({})} data-testid="create-pair"
              className="inline-flex items-center gap-1.5 h-[42px] px-4 rounded-[10px] font-bold text-[13px]"
              style={{ background: C.gold, color: C.bg }}>
              <Plus size={14} /> Create Pair
            </button>
          </div>

          <div className="mt-5 -mx-5 overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="pairs-table">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
                  {["Pair","Status","Last Price","24h Volume","Spread %","Maker/Taker Fee","Trading","Actions"].map((h, i) => (
                    <th key={h} className={`px-5 py-3 font-semibold ${i >= 2 && i <= 5 ? "text-right" : ""} ${i === 7 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: C.border }}>
                {pageRows.map((p) => {
                  const letter = p.base?.[0] || p.pair[0];
                  return (
                    <tr key={p.id} className="hover:bg-[#13192C]" data-testid={`pair-row-${p.pair.replace("/", "-")}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full grid place-items-center font-bold text-[13px]"
                            style={{ background: `${C.gold}22`, color: C.gold }}>{letter}</div>
                          <div>
                            <div className="font-bold">{p.pair}</div>
                            <div className="text-[11px]" style={{ color: C.dim }}>{p.pair}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4"><Pill value={p.status} /></td>
                      <td className="px-5 py-4 text-right tabular-nums font-bold">{fmtUSD(p.last_price, 6)}</td>
                      <td className="px-5 py-4 text-right tabular-nums" style={{ color: C.muted }}>{fmtVol(p.volume_24h)}</td>
                      <td className="px-5 py-4 text-right tabular-nums" style={{ color: C.muted }}>{((p.last_price ? 0.01 : 0) + 0).toFixed(2)}%</td>
                      <td className="px-5 py-4 text-right" style={{ color: C.muted }}>{p.maker_fee_pct}% / {p.taker_fee_pct}%</td>
                      <td className="px-5 py-4"><Pill value={p.trading_enabled ? "Yes" : "No"} /></td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex gap-1.5">
                          <IconBtn icon={Edit2} title="Edit" onClick={() => setEditing(p)} testid={`pair-edit-${p.pair.replace("/", "-")}`} />
                          <IconBtn icon={p.trading_enabled ? X : CheckCircle2}
                            color={p.trading_enabled ? C.red : C.green}
                            title={p.trading_enabled ? "Disable" : "Enable"}
                            onClick={async () => {
                              await api.patch(`/api/admin/spot/pairs/${p.id}`, { ...p, trading_enabled: !p.trading_enabled });
                              reload();
                            }} />
                          <IconBtn icon={RefreshCw} title="Reset" onClick={reload} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-12 text-center" style={{ color: C.muted }}>No pairs match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <AdminPager
            page={pager.page}
            total={filtered.length}
            pageSize={pager.pageSize}
            onPage={pager.setPage}
            testId="spot-pairs-pager"
            emptyLabel="No pairs"
            flush
          />
        </>
      )}

      {sub === "health" && (
        <div className="border-t pt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" style={{ borderColor: C.border }}>
          {health.map((h) => (
            <div key={h.pair} className="p-4 rounded-[12px] border bg-[#0A0F1F]" style={{ borderColor: C.border }}>
              <div className="flex justify-between items-center mb-3">
                <span className="font-bold">{h.pair}</span>
                <Pill value={h.status} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <Stat label="Spread" value={`${h.spread_pct.toFixed(3)}%`} color={C.green} />
                <Stat label="Depth" value={fmtVol(h.depth_usd)} />
                <Stat label="Latency" value={`${h.latency_ms}ms`} color={h.latency_ms < 12 ? C.green : C.amber} />
                <Stat label="Uptime" value={`${h.uptime_pct.toFixed(2)}%`} color={C.green} />
              </div>
            </div>
          ))}
        </div>
      )}

      {sub === "fees" && <FeesSubtab pairs={pairs} />}

      {editing !== null && <PairModal pair={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </Card>
  );
}

// ----- Fees & Commission sub-tab -----
function FeesSubtab() {
  const [fees, setFees] = useState([]);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);
  const reload = () => Promise.all([
    api.get("/api/admin/spot/fees").then(({ data }) => setFees(data)),
    api.get("/api/admin/spot/fees/history").then(({ data }) => setHistory(data)),
  ]);
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border-t pt-5 space-y-5" style={{ borderColor: C.border }}>
      <div className="rounded-[12px] border bg-[#0A0F1F]/50 p-5" style={{ borderColor: C.border }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-[15px]">Current Fee Configuration</h3>
          <button onClick={() => setEditing({})} data-testid="fees-edit-btn"
            className="inline-flex items-center gap-1.5 h-[36px] px-4 rounded-[8px] font-bold text-[12px]"
            style={{ background: C.gold, color: C.bg }}>
            <Edit2 size={12} /> Edit Fees
          </button>
        </div>
        <table className="w-full text-[13px]" data-testid="fees-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider" style={{ color: C.muted }}>
              <th className="px-2 py-3 text-left font-semibold">Market</th>
              <th className="px-2 py-3 text-right font-semibold">Maker Fee</th>
              <th className="px-2 py-3 text-right font-semibold">Taker Fee</th>
              <th className="px-2 py-3 text-right font-semibold">VIP Override</th>
              <th className="px-2 py-3 text-right font-semibold">MM Override</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: C.border }}>
            {fees.map((f) => (
              <tr key={f.pair} data-testid={`fee-row-${f.pair.replace("/", "-")}`}>
                <td className="px-2 py-4 font-bold">{f.pair}</td>
                <td className="px-2 py-4 text-right tabular-nums font-bold" style={{ color: C.green }}>{f.maker_fee_pct}%</td>
                <td className="px-2 py-4 text-right tabular-nums font-bold" style={{ color: C.gold }}>{f.taker_fee_pct}%</td>
                <td className="px-2 py-4 text-right tabular-nums" style={{ color: C.muted }}>{f.vip_override_pct}%</td>
                <td className="px-2 py-4 text-right tabular-nums" style={{ color: C.muted }}>{f.mm_override_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="font-bold text-[15px] mb-3">Fee Change History</h3>
        <div className="space-y-3" data-testid="fees-history">
          {history.map((h) => (
            <div key={h.id} className="rounded-[12px] border bg-[#0A0F1F] p-4 flex items-start justify-between" style={{ borderColor: C.border }}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[14px] mb-2">{h.market}</div>
                <div className="text-[12px] flex flex-wrap gap-x-4 gap-y-1" style={{ color: C.muted }}>
                  <span>Maker: <span style={{ color: C.text }}>{h.old_maker_pct}% → {h.new_maker_pct}%</span></span>
                  <span style={{ color: C.dim }}>|</span>
                  <span>Taker: <span style={{ color: C.text }}>{h.old_taker_pct}% → {h.new_taker_pct}%</span></span>
                </div>
                <div className="text-[11px] mt-2" style={{ color: C.dim }}>Effective from: {h.effective_from}</div>
              </div>
              <div className="text-right text-[11px]" style={{ color: C.dim }}>
                <div style={{ color: C.muted }}>{h.actor_email}</div>
                <div className="mt-1">{(h.changed_at || "").slice(0, 10)}</div>
              </div>
            </div>
          ))}
          {history.length === 0 && (
            <div className="p-8 text-center text-[12px]" style={{ color: C.muted }}>No fee changes yet.</div>
          )}
        </div>
      </div>

      {editing && <FeeEditModal pairs={fees} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function FeeEditModal({ pairs, onClose, onSaved }) {
  const [market, setMarket] = useState(pairs[0]?.pair || "");
  const [maker, setMaker] = useState("");
  const [taker, setTaker] = useState("");
  useEffect(() => {
    const p = pairs.find((p) => p.pair === market);
    if (p) { setMaker(String(p.maker_fee_pct)); setTaker(String(p.taker_fee_pct)); }
  }, [market, pairs]);

  async function save(e) {
    e.preventDefault();
    const [a, b] = market.split("/");
    await api.patch(`/api/admin/spot/fees/${a}/${b}`, {
      maker_fee_pct: Number(maker), taker_fee_pct: Number(taker),
    });
    onSaved();
  }
  return (
    <DetailModal title="Edit Trading Fees" onClose={onClose} testid="fee-edit-modal">
      <form onSubmit={save} className="space-y-5">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Select Market</span>
          <select value={market} onChange={(e) => setMarket(e.target.value)}
            data-testid="fee-edit-market"
            className="w-full h-[44px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[14px] outline-none"
            style={{ borderColor: C.border, color: C.text }}>
            {pairs.map((p) => <option key={p.pair} value={p.pair}>{p.pair}</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Maker Fee (%)</span>
            <input type="number" step="0.001" required value={maker} onChange={(e) => setMaker(e.target.value)}
              data-testid="fee-edit-maker"
              className="w-full h-[44px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[14px] outline-none"
              style={{ borderColor: C.border, color: C.text }} />
          </label>
          <label className="block">
            <span className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Taker Fee (%)</span>
            <input type="number" step="0.001" required value={taker} onChange={(e) => setTaker(e.target.value)}
              data-testid="fee-edit-taker"
              className="w-full h-[44px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[14px] outline-none"
              style={{ borderColor: C.border, color: C.text }} />
          </label>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-[44px] rounded-[10px] border font-bold text-[13px]"
            style={{ borderColor: C.border, color: C.text, background: "transparent" }}>Cancel</button>
          <button type="submit" data-testid="fee-edit-save"
            className="flex-1 h-[44px] rounded-[10px] font-bold text-[13px]"
            style={{ background: C.gold, color: C.bg }}>Update Fees</button>
        </div>
      </form>
    </DetailModal>
  );
}

// ----- Generic detail modal shell -----
function DetailModal({ title, onClose, children, testid, maxWidth = 560 }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={onClose} data-testid={testid}>
      <div className="w-full rounded-[16px] border" style={{ maxWidth, background: C.surface, borderColor: C.border }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.border }}>
          <h3 className="font-bold text-[16px]">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md hover:bg-[#1A2238]"
            style={{ color: C.muted }} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div className="rounded-[10px] border bg-[#0A0F1F] p-3.5" style={{ borderColor: C.border }}>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.muted }}>{label}</div>
      <div className="font-bold text-[15px] break-all">{children}</div>
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between items-center py-3 border-b last:border-0" style={{ borderColor: C.border }}>
      <span style={{ color: C.muted }} className="text-[13px]">{label}</span>
      <span className="font-bold text-right">{children}</span>
    </div>
  );
}

function Stat({ label, value, color = C.text }) {
  return (
    <div>
      <div style={{ color: C.dim }} className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, color = C.muted, title, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
      className="w-8 h-8 rounded-md grid place-items-center transition hover:opacity-80"
      style={{ background: `${color}10`, color, border: `1px solid ${color}33` }}>
      <Icon size={13} />
    </button>
  );
}

// ----- Pair create/edit modal -----
function PairModal({ pair, onClose, onSaved }) {
  const isEdit = !!pair?.id;
  const [f, setF] = useState({
    pair: pair?.pair || "", base: pair?.base || "", quote: pair?.quote || "USDT",
    status: pair?.status || "active",
    maker_fee_pct: pair?.maker_fee_pct ?? 0.1,
    taker_fee_pct: pair?.taker_fee_pct ?? 0.1,
    min_order: pair?.min_order ?? 0,
    trading_enabled: pair?.trading_enabled ?? true,
    last_price: pair?.last_price ?? 0,
    volume_24h: pair?.volume_24h ?? 0,
  });
  async function save(e) {
    e.preventDefault();
    if (isEdit) await api.patch(`/api/admin/spot/pairs/${pair.id}`, f);
    else await api.post("/api/admin/spot/pairs", f);
    onSaved();
  }
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-black/70" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] rounded-[14px] border p-6 space-y-4"
        style={{ background: C.surface, borderColor: C.border, color: C.text }}
        data-testid="pair-modal">
        <h3 className="text-[18px] font-bold">{isEdit ? "Edit pair" : "Create pair"}</h3>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Pair" v={f.pair} on={(v) => setF({ ...f, pair: v })} ph="BTC/USDT" />
          <Field label="Base" v={f.base} on={(v) => setF({ ...f, base: v })} ph="BTC" />
          <Field label="Quote" v={f.quote} on={(v) => setF({ ...f, quote: v })} ph="USDT" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Maker fee %" type="number" step="0.01" v={f.maker_fee_pct} on={(v) => setF({ ...f, maker_fee_pct: Number(v) })} />
          <Field label="Taker fee %" type="number" step="0.01" v={f.taker_fee_pct} on={(v) => setF({ ...f, taker_fee_pct: Number(v) })} />
          <Field label="Min order" type="number" v={f.min_order} on={(v) => setF({ ...f, min_order: Number(v) })} />
        </div>
        <Select label="Status" v={f.status} on={(v) => setF({ ...f, status: v })} options={["active","halted","maintenance"]} />
        <button type="submit" data-testid="pair-save"
          className="w-full h-[42px] rounded-[10px] font-bold"
          style={{ background: C.gold, color: C.bg }}>Save</button>
      </form>
    </div>
  );
}

function Field({ label, v, on, ph, type = "text", step }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: C.dim }}>{label}</span>
      <input type={type} step={step} value={v ?? ""} placeholder={ph}
        onChange={(e) => on(e.target.value)}
        className="w-full h-[40px] px-3 rounded-[8px] border bg-[#0A0F1F] text-[13px] outline-none focus:border-[#F0B83A]"
        style={{ borderColor: C.border, color: C.text }} />
    </label>
  );
}
function Select({ label, v, on, options }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: C.dim }}>{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)}
        className="w-full h-[40px] px-3 rounded-[8px] border bg-[#0A0F1F] text-[13px] outline-none"
        style={{ borderColor: C.border, color: C.text }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

// ============================================================================
// ORDER BOOK (LIVE)
// ============================================================================
function OrderBookTab() {
  const [pairs, setPairs] = useState([]);
  const [pair, setPair] = useState("BTC/USDT");
  const [book, setBook] = useState(null);
  useEffect(() => { api.get("/api/admin/spot/pairs").then(({ data }) => setPairs(asAdminList(data).filter((p) => p.status === "active"))); }, []);
  useEffect(() => {
    if (!pair) return;
    let cancelled = false;
    const load = () => {
      const [a, b] = pair.split("/");
      api.get(`/api/admin/spot/orderbook/${a}/${b}`).then(({ data }) => { if (!cancelled) setBook(data); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pair]);

  if (!book) return <Card className="p-12 text-center" ><span style={{ color: C.muted }}>Loading order book…</span></Card>;
  const maxBid = Math.max(...book.bids.map((b) => b.qty), 0.001);
  const maxAsk = Math.max(...book.asks.map((a) => a.qty), 0.001);

  return (
    <Card className="p-5">
      {/* header strip */}
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="relative">
          <select value={pair} onChange={(e) => setPair(e.target.value)}
            data-testid="orderbook-pair"
            className="appearance-none h-[42px] pl-3 pr-9 rounded-[10px] border bg-[#0A0F1F] text-[13px] font-bold"
            style={{ borderColor: C.border, color: C.text }}>
            {pairs.map((p) => <option key={p.id} value={p.pair}>{p.pair}</option>)}
          </select>
          <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.dim }} />
        </div>
        <span className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-[8px] text-[12px] font-bold"
          style={{ background: "rgba(16,185,129,0.15)", color: C.green }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.green }} />
          LIVE (2s refresh)
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-5 text-[13px]">
          <Metric label="Spread" value={`$${book.spread.toFixed(2)}`} />
          <Metric label="Spread %" value={`${book.spread_pct.toFixed(4)}%`} />
          <Metric label="Best Bid" value={`$${book.best_bid.toLocaleString()}`} color={C.green} />
          <Metric label="Best Ask" value={`$${book.best_ask.toLocaleString()}`} color={C.red} />
        </div>
      </div>

      {/* two side-by-side panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BookSide side="bids" rows={book.bids} max={maxBid} total={book.bid_total} accent={C.green} symbol={pair.split("/")[0]} />
        <BookSide side="asks" rows={book.asks} max={maxAsk} total={book.ask_total} accent={C.red} symbol={pair.split("/")[0]} />
      </div>
    </Card>
  );
}

function Metric({ label, value, color = C.text }) {
  return (
    <div className="flex items-center gap-2">
      <span style={{ color: C.muted }}>{label}:</span>
      <span className="font-bold tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

function BookSide({ side, rows, max, total, accent, symbol }) {
  return (
    <div className="rounded-[12px] border bg-[#0A0F1F] p-4" style={{ borderColor: C.border }}>
      <div className="flex justify-between items-baseline mb-3">
        <h3 className="font-bold text-[14px]" style={{ color: accent }}>
          {side === "bids" ? "Bids (Buy Orders)" : "Asks (Sell Orders)"}
        </h3>
        <span className="text-[11px]" style={{ color: C.muted }}>Total: {total.toFixed(4)} {symbol}</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-[10px] uppercase tracking-wider mb-2 px-1" style={{ color: C.muted }}>
        <span>Price (USD)</span>
        <span className="text-right">Quantity</span>
        <span className="text-right">Total</span>
        <span className="text-right">Orders</span>
      </div>
      <div className="space-y-px font-mono text-[12px]">
        {rows.map((r, i) => {
          const w = (r.qty / max) * 100;
          return (
            <div key={i} className="relative grid grid-cols-4 gap-2 px-1 py-1.5 rounded">
              <div className="absolute inset-0 rounded" style={{ background: `${accent}15`, width: `${w}%` }} />
              <span className="relative tabular-nums font-bold" style={{ color: accent }}>${r.price.toLocaleString()}</span>
              <span className="relative text-right tabular-nums">{r.qty.toFixed(4)}</span>
              <span className="relative text-right tabular-nums" style={{ color: C.muted }}>${r.total.toLocaleString()}</span>
              <span className="relative text-right tabular-nums" style={{ color: C.muted }}>{r.orders}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// ORDERS
// ============================================================================
function OrdersTab() {
  const [sub, setSub] = useState("open");
  const [orders, setOrders] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [userId, setUserId] = useState("");
  const [market, setMarket] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [viewing, setViewing] = useState(null);
  const pager = useAdminPager();

  useEffect(() => {
    api.get("/api/admin/spot/pairs").then(({ data }) => setPairs(asAdminList(data)));
  }, []);
  const reload = () => {
    setLoading(true);
    api.get("/api/admin/spot/orders", {
      params: { status: sub, user_id: userId || undefined, market, skip: pager.skip, limit: pager.limit },
    })
      .then(({ data }) => {
        const { items, total } = unwrapAdminList(data, "orders");
        setOrders(asAdminList(items).slice(0, pager.pageSize));
        setListTotal(total);
        pager.setTotal(total);
      })
      .catch(() => {
        setOrders([]);
        setListTotal(0);
        pager.setTotal(0);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { pager.resetPage(); }, [sub, market, pager.resetPage]);
  useEffect(reload, [sub, market, pager.page]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => { setUserId(""); setMarket("all"); };

  async function cancelOne(id) {
    await api.post(`/api/admin/spot/orders/${id}/cancel`);
    reload();
  }
  async function cancelAll() {
    if (!window.confirm(`Cancel ALL ${listTotal} open orders${market !== "all" ? ` for ${market}` : ""}?`)) return;
    await api.post("/api/admin/spot/orders/bulk-cancel", market !== "all" ? { market } : {});
    reload();
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <PillTabs tabs={[{ id: "open", label: "Open Orders" }, { id: "cancelled", label: "Cancelled Orders" }]}
          value={sub} onChange={setSub} testidPrefix="orders-sub" />
        <div className="ml-auto flex gap-2">
          {sub === "open" && (
            <GhostBtn color={C.red} onClick={cancelAll} testid="orders-bulk-cancel">
              <XCircle size={13} /> Bulk Cancel
            </GhostBtn>
          )}
          <GhostBtn color={C.gold} onClick={reload} testid="orders-refresh">
            <RefreshCw size={13} /> Refresh
          </GhostBtn>
        </div>
      </div>

      {/* filter row */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input value={userId} onChange={(e) => setUserId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && reload()}
          placeholder="User ID" data-testid="orders-user-id"
          className="h-[42px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[13px] outline-none w-[180px]"
          style={{ borderColor: C.border, color: C.text }} />
        <div className="relative">
          <select value={market} onChange={(e) => setMarket(e.target.value)}
            data-testid="orders-market-filter"
            className="appearance-none h-[42px] pl-3 pr-9 rounded-[10px] border bg-[#0A0F1F] text-[13px]"
            style={{ borderColor: C.border, color: C.text }}>
            <option value="all">All Markets</option>
            {pairs.map((p) => <option key={p.id} value={p.pair}>{p.pair}</option>)}
          </select>
          <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.dim }} />
        </div>
        <button onClick={reset} className="inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: C.gold }}>
          <RefreshCw size={12} /> Reset
        </button>
        <span className="ml-auto text-[12px]" style={{ color: C.muted }}>Total: {listTotal} orders</span>
      </div>

      <div className="-mx-5 overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="orders-table">
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
              {["Order ID","User","Market","Side","Type","Price","Qty / Filled","Locked","Status","Actions"].map((h, i) => (
                <th key={h} className={`px-5 py-3 font-semibold ${i >= 5 && i <= 7 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: C.border }}>
            {loading && <tr><td colSpan={10} className="p-12 text-center" style={{ color: C.muted }}>Loading…</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={10} className="p-12 text-center" style={{ color: C.muted }}>No {sub} orders.</td></tr>
            )}
            {orders.map((o) => {
              const fillPct = o.qty > 0 ? Math.min(100, (o.filled / o.qty) * 100) : 0;
              return (
                <tr key={o.order_id} className="hover:bg-[#13192C]" data-testid={`order-row-${o.order_id}`}>
                  <td className="px-5 py-4 font-mono text-[11px]"><div>{o.order_id.split("-")[0]}-</div><div>{o.order_id.split("-")[1]}</div></td>
                  <td className="px-5 py-4">
                    <div className="font-bold">{o.user_id}</div>
                    <div className="text-[11px] truncate max-w-[180px]" style={{ color: C.dim }}>{o.user_email}</div>
                  </td>
                  <td className="px-5 py-4 font-bold">{o.market}</td>
                  <td className="px-5 py-4"><Pill value={o.side.toUpperCase()} /></td>
                  <td className="px-5 py-4 capitalize">{o.type}</td>
                  <td className="px-5 py-4 text-right tabular-nums">${Number(o.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="tabular-nums">{o.qty} /</div>
                    <div className="tabular-nums" style={{ color: C.green }}>{o.filled}</div>
                    <div className="h-1 mt-1 rounded-full overflow-hidden" style={{ background: C.border }}>
                      <div className="h-full" style={{ width: `${fillPct}%`, background: C.green }} />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="tabular-nums font-bold" style={{ color: C.gold }}>{Number(o.locked_amount).toLocaleString()}</span>
                    <span className="text-[10px] ml-1" style={{ color: C.dim }}>{o.locked_asset}</span>
                  </td>
                  <td className="px-5 py-4"><Pill value={o.status} /></td>
                  <td className="px-5 py-4">
                    <div className="inline-flex gap-1.5">
                      <IconBtn icon={Eye} title="View" testid={`order-view-${o.order_id}`} onClick={() => setViewing(o)} />
                      {o.status === "open" && (
                        <IconBtn icon={X} color={C.red} title="Cancel" testid={`order-cancel-${o.order_id}`} onClick={() => cancelOne(o.order_id)} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} loading={loading} onPage={pager.setPage} testId="spot-orders-pager" emptyLabel="No orders" flush />

      {viewing && (
        <OrderDetailsModal order={viewing}
          onClose={() => setViewing(null)}
          onCancel={async () => { await cancelOne(viewing.order_id); setViewing(null); }} />
      )}
    </Card>
  );
}

function OrderDetailsModal({ order, onClose, onCancel }) {
  const total = (Number(order.price) || 0) * (Number(order.qty) || 0);
  return (
    <DetailModal title="Order Details" onClose={onClose} testid="order-details-modal">
      <div className="grid grid-cols-2 gap-3">
        <DetailField label="Order ID">{order.order_id}</DetailField>
        <DetailField label="User ID">{order.user_id}</DetailField>
        <DetailField label="Symbol">{order.market}</DetailField>
        <DetailField label="Side"><Pill value={order.side.toUpperCase()} /></DetailField>
        <DetailField label="Type"><span className="capitalize">{order.type}</span></DetailField>
        <DetailField label="Status"><Pill value={order.status} /></DetailField>
        <DetailField label="Quantity">{order.qty}</DetailField>
        <DetailField label="Price"><span style={{ color: C.gold }}>${Number(order.price).toLocaleString()}</span></DetailField>
        <DetailField label="Filled">{order.filled} / {order.qty}</DetailField>
        <DetailField label="Total Value">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</DetailField>
      </div>
      <div className="flex gap-3 pt-5 mt-2">
        <button onClick={onClose}
          className="flex-1 h-[44px] rounded-[10px] border font-bold text-[13px]"
          style={{ borderColor: C.border, color: C.text }} data-testid="order-modal-close">Close</button>
        {order.status === "open" && (
          <button onClick={onCancel} data-testid="order-modal-cancel"
            className="flex-1 h-[44px] rounded-[10px] font-bold text-[13px]"
            style={{ background: "#FB5C73", color: "#fff" }}>Cancel Order</button>
        )}
      </div>
    </DetailModal>
  );
}

// ============================================================================
// TRADES
// ============================================================================
function TradesTab() {
  const [pairs, setPairs] = useState([]);
  const [market, setMarket] = useState("all");
  const [userType, setUserType] = useState("all");
  const [flagged, setFlagged] = useState(false);
  const [trades, setTrades] = useState([]);
  const [viewing, setViewing] = useState(null);
  const pager = useAdminPager();
  const reload = () => {
    api.get("/api/admin/spot/trades", {
      params: { market, user_type: userType, flagged, skip: pager.skip, limit: pager.limit },
    }).then(({ data }) => {
      const { items, total } = unwrapAdminList(data, "trades");
      setTrades(asAdminList(items).slice(0, pager.pageSize));
      pager.setTotal(total);
    });
  };
  useEffect(() => { api.get("/api/admin/spot/pairs").then(({ data }) => setPairs(asAdminList(data))); }, []);
  useEffect(() => { pager.resetPage(); }, [market, userType, flagged, pager.resetPage]);
  useEffect(() => { reload(); }, [market, userType, flagged, pager.page]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <SelectBox value={market} onChange={setMarket} testid="trades-market" >
          <option value="all">All Markets</option>
          {pairs.map((p) => <option key={p.id} value={p.pair}>{p.pair}</option>)}
        </SelectBox>
        <SelectBox value={userType} onChange={setUserType} testid="trades-user-type" >
          <option value="all">All User Types</option>
          <option value="retail">Retail</option>
          <option value="institutional">Institutional</option>
          <option value="market_maker">Market Maker</option>
        </SelectBox>
        <label className="inline-flex items-center gap-2 text-[12px] cursor-pointer" data-testid="trades-flagged-only">
          <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} className="accent-[#F0B83A]" />
          <span style={{ color: C.text }}>Flagged Only</span>
        </label>
        <span className="ml-auto inline-flex items-center gap-1.5 h-[34px] px-3 rounded-[8px] text-[12px] font-bold"
          style={{ background: "rgba(16,185,129,0.15)", color: C.green }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.green }} />
          Live Feed
        </span>
      </div>

      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold">Executed Trades</h3>
        <GhostBtn color={C.gold} onClick={reload} testid="trades-refresh"><RefreshCw size={13} /> Refresh</GhostBtn>
      </div>

      <div className="-mx-5 overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="trades-table">
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
              {["Trade ID","Market","Price","Quantity","Value","Maker","Taker","Fee","Status","Actions"].map((h, i) => (
                <th key={h} className={`px-5 py-3 font-semibold ${[2, 3, 4, 7].includes(i) ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: C.border }}>
            {trades.map((t) => (
              <tr key={t.trade_id} className="hover:bg-[#13192C]">
                <td className="px-5 py-4 font-mono text-[11px]"><div>{t.trade_id.split("-")[0]}-</div><div>{t.trade_id.split("-")[1]}</div></td>
                <td className="px-5 py-4 font-bold">{t.market}</td>
                <td className="px-5 py-4 text-right tabular-nums">${Number(t.price).toLocaleString()}</td>
                <td className="px-5 py-4 text-right tabular-nums">{t.qty}</td>
                <td className="px-5 py-4 text-right tabular-nums font-bold" style={{ color: C.green }}>${t.value.toFixed(2)}</td>
                <td className="px-5 py-4">
                  <div className="font-bold">{t.maker_user_id}</div>
                  <div className="text-[11px] truncate max-w-[160px]" style={{ color: C.dim }}>{t.maker_email}</div>
                </td>
                <td className="px-5 py-4">
                  <div className="font-bold">{t.taker_user_id}</div>
                  <div className="text-[11px] truncate max-w-[160px]" style={{ color: C.dim }}>{t.taker_email}</div>
                </td>
                <td className="px-5 py-4 text-right tabular-nums font-bold" style={{ color: C.gold }}>${t.fee.toFixed(4)}</td>
                <td className="px-5 py-4"><Pill value={t.status} /></td>
                <td className="px-5 py-4"><IconBtn icon={Eye} title="View" testid={`trade-view-${t.trade_id}`} onClick={() => setViewing(t)} /></td>
              </tr>
            ))}
            {trades.length === 0 && <tr><td colSpan={10} className="p-12 text-center" style={{ color: C.muted }}>No trades match filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="spot-trades-pager" emptyLabel="No trades" flush />
      {viewing && <TradeDetailsModal trade={viewing} onClose={() => setViewing(null)} />}
    </Card>
  );
}

function TradeDetailsModal({ trade, onClose }) {
  return (
    <DetailModal title="Trade Details" onClose={onClose} testid="trade-details-modal" maxWidth={520}>
      <div className="rounded-[10px] border bg-[#0A0F1F] divide-y px-5" style={{ borderColor: C.border }}>
        <DetailRow label="Trade ID">{trade.trade_id}</DetailRow>
        <DetailRow label="Market">{trade.market}</DetailRow>
        <DetailRow label="Price"><span style={{ color: C.gold }}>${Number(trade.price).toLocaleString()}</span></DetailRow>
        <DetailRow label="Quantity">{Number(trade.qty).toFixed(6)}</DetailRow>
        <DetailRow label="Quote Value"><span style={{ color: C.green }}>${Number(trade.value).toFixed(2)}</span></DetailRow>
        <DetailRow label="Maker">
          <div>{trade.maker_user_id}</div>
          <div className="text-[11px] font-normal" style={{ color: C.dim }}>{trade.maker_email}</div>
        </DetailRow>
        <DetailRow label="Taker">
          <div>{trade.taker_user_id}</div>
          <div className="text-[11px] font-normal" style={{ color: C.dim }}>{trade.taker_email}</div>
        </DetailRow>
        <DetailRow label="Maker Fee"><span style={{ color: C.gold }}>${(trade.fee / 2).toFixed(4)}</span></DetailRow>
        <DetailRow label="Taker Fee"><span style={{ color: C.gold }}>${(trade.fee / 2).toFixed(4)}</span></DetailRow>
        <DetailRow label="Settlement"><Pill value={trade.status} /></DetailRow>
        <DetailRow label="Executed At">{new Date(trade.created_at).toLocaleString()}</DetailRow>
      </div>
      <div className="mt-4 p-3 rounded-[10px] border text-[12px]"
        style={{ borderColor: `${C.gold}40`, background: `${C.gold}10`, color: C.gold }}>
        Trades are immutable. For disputes, contact compliance team.
      </div>
    </DetailModal>
  );
}

function SelectBox({ value, onChange, children, testid }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="appearance-none h-[40px] pl-3 pr-9 rounded-[10px] border bg-[#0A0F1F] text-[13px]"
        style={{ borderColor: C.border, color: C.text }}>
        {children}
      </select>
      <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: C.dim }} />
    </div>
  );
}

// ============================================================================
// LIQUIDITY
// ============================================================================
function LiquidityTab() {
  const [kpis, setKpis] = useState({});
  const [configs, setConfigs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [editing, setEditing] = useState(null);
  const configPager = useAdminPager();
  const reload = () => Promise.all([
    api.get("/api/admin/spot/liquidity/kpis").then(({ data }) => setKpis(data)),
    api.get("/api/admin/spot/liquidity/configs").then(({ data }) => {
      const list = asAdminList(data);
      setConfigs(list);
      configPager.setTotal(list.length);
    }),
    api.get("/api/admin/spot/liquidity/activity").then(({ data }) => setActivity(asAdminList(data))),
    api.get("/api/admin/spot/liquidity/feeds").then(({ data }) => setFeeds(asAdminList(data))),
  ]);
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleMM(c) {
    const [a, b] = c.market.split("/");
    await api.patch(`/api/admin/spot/liquidity/configs/${a}/${b}`, { mm_enabled: !c.mm_enabled });
    reload();
  }

  const pageConfigs = asAdminList(configs).slice(configPager.skip, configPager.skip + configPager.pageSize);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GradKpi tint="yellow" label="Active Market Makers" value={kpis.active_market_makers ?? 0} icon={Zap} />
        <GradKpi tint="blue" label="Total Liquidity Configs" value={kpis.total_configs ?? 0} icon={Activity} />
        <GradKpi tint="green" label="Avg Target Spread" value={`${(kpis.avg_target_spread_pct ?? 0).toFixed(2)}%`} icon={TrendingUp} />
        <GradKpi tint="purple" label="External Feeds" value={kpis.external_feeds ?? 0} icon={Globe2} />
      </div>

      {/* Configs */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2"><span style={{ color: C.gold }}>$</span>Market Liquidity Configurations</h3>
          <GhostBtn color={C.gold} onClick={reload}><RefreshCw size={13} /> Refresh</GhostBtn>
        </div>
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="liquidity-configs-table">
            <thead>
              <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
                {["Market","MM Enabled","Min Bid Depth","Min Ask Depth","Target Spread","Slippage Tol.","Int/Ext Ratio","Actions"].map((h, i) => (
                  <th key={h} className={`px-5 py-3 font-semibold ${[2, 3, 4, 5].includes(i) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: C.border }}>
              {pageConfigs.map((c) => (
                <tr key={c.market} className="hover:bg-[#13192C]">
                  <td className="px-5 py-4 font-bold">{c.market}</td>
                  <td className="px-5 py-4">
                    <button onClick={() => toggleMM(c)} data-testid={`mm-toggle-${c.market.replace("/","-")}`}>
                      <Pill value={c.mm_enabled ? "ENABLED" : "DISABLED"} />
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right tabular-nums">{c.min_bid_depth}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{c.min_ask_depth}</td>
                  <td className="px-5 py-4 text-right tabular-nums font-bold" style={{ color: C.gold }}>{c.target_spread_pct}%</td>
                  <td className="px-5 py-4 text-right tabular-nums" style={{ color: C.muted }}>{c.slippage_tol_pct}%</td>
                  <td className="px-5 py-4">
                    <span className="font-bold" style={{ color: C.green }}>{c.internal_ratio_pct}%</span>
                    <span style={{ color: C.muted }}> / </span>
                    <span className="font-bold" style={{ color: C.blue }}>{c.external_ratio_pct}%</span>
                  </td>
                  <td className="px-5 py-4"><IconBtn icon={Settings} title="Configure"
                    testid={`liquidity-edit-${c.market.replace("/", "-")}`}
                    onClick={() => setEditing(c)} /></td>
                </tr>
              ))}
              {pageConfigs.length === 0 && (
                <tr><td colSpan={8} className="p-12 text-center" style={{ color: C.muted }}>No liquidity configs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <AdminPager
          page={configPager.page}
          total={configs.length}
          pageSize={configPager.pageSize}
          onPage={configPager.setPage}
          testId="spot-liquidity-pager"
          emptyLabel="No configs"
          flush
        />
      </Card>

      {/* Activity */}
      <Card className="p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Activity size={14} style={{ color: C.blue }} /> Market Maker Activity (24h)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activity.map((a) => (
            <div key={a.market} className="p-5 rounded-[12px] border bg-[#0A0F1F]" style={{ borderColor: C.border }}>
              <div className="font-bold text-[15px] mb-3">{a.market}</div>
              <div className="space-y-2 text-[13px]">
                <Row label="Orders Placed:" value={a.orders_placed.toLocaleString()} />
                <Row label="Volume Provided:" value={`$${(a.volume_provided_usd / 1000).toFixed(0)}K`} color={C.green} />
                <Row label="Spread Maintained:" value={`${a.spread_maintained_pct}%`} color={C.gold} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* External feeds */}
      <Card className="p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Globe2 size={14} style={{ color: C.blue }} /> External Price Feeds</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {feeds.map((f) => (
            <div key={f.name} className="p-4 rounded-[12px] border bg-[#0A0F1F] flex items-center justify-between" style={{ borderColor: C.border }}>
              <div>
                <div className="font-bold">{f.name}</div>
                <div className="text-[11px]" style={{ color: C.dim }}>Latency: {f.latency_ms}ms</div>
              </div>
              <Pill value={f.status} />
            </div>
          ))}
        </div>
      </Card>
      {editing && (
        <LiquidityConfigModal config={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function LiquidityConfigModal({ config, onClose, onSaved }) {
  const [f, setF] = useState({
    min_bid_depth: config.min_bid_depth,
    min_ask_depth: config.min_ask_depth,
    target_spread_pct: config.target_spread_pct,
    slippage_tol_pct: config.slippage_tol_pct,
    internal_ratio_pct: config.internal_ratio_pct,
    external_ratio_pct: config.external_ratio_pct,
    mm_enabled: config.mm_enabled,
  });
  async function save(e) {
    e.preventDefault();
    const [a, b] = config.market.split("/");
    await api.patch(`/api/admin/spot/liquidity/configs/${a}/${b}`, f);
    onSaved();
  }
  return (
    <DetailModal title={`Liquidity Configuration - ${config.market}`} onClose={onClose} testid="liquidity-modal">
      <form onSubmit={save} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Min Bid Depth" value={f.min_bid_depth}    on={(v) => setF({ ...f, min_bid_depth: Number(v) })} testid="liq-min-bid" />
          <NumField label="Min Ask Depth" value={f.min_ask_depth}    on={(v) => setF({ ...f, min_ask_depth: Number(v) })} testid="liq-min-ask" />
          <NumField label="Target Spread (%)" step="0.01" value={f.target_spread_pct} on={(v) => setF({ ...f, target_spread_pct: Number(v) })} testid="liq-spread" />
          <NumField label="Slippage Tolerance (%)" step="0.01" value={f.slippage_tol_pct} on={(v) => setF({ ...f, slippage_tol_pct: Number(v) })} testid="liq-slippage" />
          <NumField label="Internal Liquidity (%)" value={f.internal_ratio_pct} on={(v) => setF({ ...f, internal_ratio_pct: Number(v) })} testid="liq-internal" />
          <NumField label="External Liquidity (%)" value={f.external_ratio_pct} on={(v) => setF({ ...f, external_ratio_pct: Number(v) })} testid="liq-external" />
        </div>
        <div>
          <span className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Market Maker Status</span>
          <button type="button" onClick={() => setF({ ...f, mm_enabled: !f.mm_enabled })}
            data-testid="liq-mm-toggle"
            className="w-full h-[52px] px-4 rounded-[10px] border bg-[#0A0F1F] flex items-center justify-between"
            style={{ borderColor: C.border }}>
            <span className="text-[14px] font-bold" style={{ color: C.text }}>Market Maker</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md"
              style={{
                color: f.mm_enabled ? "#fff" : C.muted,
                background: f.mm_enabled ? C.green : "rgba(136,146,168,0.15)",
              }}>
              {f.mm_enabled ? "ENABLED" : "DISABLED"}
            </span>
          </button>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-[44px] rounded-[10px] border font-bold text-[13px]"
            style={{ borderColor: C.border, color: C.text }}>Cancel</button>
          <button type="submit" data-testid="liq-save"
            className="flex-1 h-[44px] rounded-[10px] font-bold text-[13px]"
            style={{ background: C.gold, color: C.bg }}>Save Changes</button>
        </div>
      </form>
    </DetailModal>
  );
}

function NumField({ label, value, on, step = "1", testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>{label}</span>
      <input type="number" step={step} value={value} onChange={(e) => on(e.target.value)}
        data-testid={testid}
        className="w-full h-[44px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[14px] outline-none focus:border-[#F0B83A]"
        style={{ borderColor: C.border, color: C.text }} />
    </label>
  );
}

function Row({ label, value, color = C.text }) {
  return (
    <div className="flex justify-between items-center">
      <span style={{ color: C.muted }}>{label}</span>
      <span className="font-bold tabular-nums" style={{ color }}>{value}</span>
    </div>
  );
}

// ============================================================================
// RISK & ALERTS
// ============================================================================
function RiskTab() {
  const [kpis, setKpis] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [viewing, setViewing] = useState(null);
  const pager = useAdminPager();
  const reload = () => Promise.all([
    api.get("/api/admin/spot/risk/kpis").then(({ data }) => setKpis(data)),
    api.get("/api/admin/spot/risk/alerts").then(({ data }) => {
      const list = asAdminList(data);
      setAlerts(list);
      pager.setTotal(list.length);
    }),
  ]);
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function resolve(id) {
    await api.post(`/api/admin/spot/risk/alerts/${id}/resolve`);
    reload();
  }
  async function emergency(action, label) {
    if (!window.confirm(`${label}? This affects all live markets.`)) return;
    await api.post(`/api/admin/spot/risk/emergency/${action}`);
    reload();
    alert(`${label} executed successfully.`);
  }

  const pageAlerts = asAdminList(alerts).slice(pager.skip, pager.skip + pager.pageSize);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GradKpi tint="red"    label="High Severity"    value={kpis.high ?? 0}   testid="risk-kpi-high" />
        <GradKpi tint="yellow" label="Medium Severity"  value={kpis.medium ?? 0} testid="risk-kpi-medium" />
        <GradKpi tint="green"  label="Low Severity"     value={kpis.low ?? 0}    testid="risk-kpi-low" />
        <GradKpi tint="blue"   label="Resolved (24h)"   value={kpis.resolved_24h ?? 0} testid="risk-kpi-resolved" />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Active Risk Alerts</h3>
          <GhostBtn color={C.gold} onClick={reload}><RefreshCw size={13} /> Refresh</GhostBtn>
        </div>
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="risk-alerts-table">
            <thead>
              <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
                {["Alert ID","Type","Severity","Market","Level","Description","Status","Actions"].map((h) => (
                  <th key={h} className="px-5 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: C.border }}>
              {pageAlerts.map((a) => (
                <tr key={a.alert_id} className="hover:bg-[#13192C]">
                  <td className="px-5 py-4 font-mono text-[11px] max-w-[180px] break-all" style={{ color: C.muted }}>{a.alert_id}</td>
                  <td className="px-5 py-4 font-bold">{a.type}</td>
                  <td className="px-5 py-4"><Pill value={a.severity} /></td>
                  <td className="px-5 py-4 font-bold">{a.market}</td>
                  <td className="px-5 py-4" style={{ color: C.muted }}>{a.level}</td>
                  <td className="px-5 py-4 max-w-[280px] truncate" style={{ color: C.muted }}>{a.description}</td>
                  <td className="px-5 py-4"><Pill value={a.status} /></td>
                  <td className="px-5 py-4">
                    <div className="inline-flex gap-1.5">
                      <IconBtn icon={Eye} title="View" testid={`risk-view-${a.alert_id}`} onClick={() => setViewing(a)} />
                      <IconBtn icon={ExternalLink} title="Open in market" />
                      {a.status === "open" && (
                        <IconBtn icon={CheckCircle2} color={C.green} title="Resolve"
                          testid={`risk-resolve-${a.alert_id}`} onClick={() => resolve(a.alert_id)} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {pageAlerts.length === 0 && <tr><td colSpan={8} className="p-12 text-center" style={{ color: C.muted }}>No alerts.</td></tr>}
            </tbody>
          </table>
        </div>
        <AdminPager
          page={pager.page}
          total={alerts.length}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="spot-risk-pager"
          emptyLabel="No alerts"
          flush
        />
      </Card>

      <Card className="p-5">
        <h3 className="font-bold mb-4">Emergency Actions</h3>
        <div className="flex flex-wrap gap-3">
          <EmergencyBtn icon={XCircle} color={C.red} label="Halt All Markets"
            testid="emergency-halt-all" onClick={() => emergency("halt-all", "Halt All Markets")} />
          <EmergencyBtn icon={XCircle} color={C.red} label="Cancel All Open Orders"
            testid="emergency-cancel-all" onClick={() => emergency("cancel-all-orders", "Cancel All Open Orders")} />
          <EmergencyBtn icon={Lock} color={C.gold} label="Disable New Orders"
            testid="emergency-disable-new" onClick={() => emergency("disable-new-orders", "Disable New Orders")} />
        </div>
      </Card>
      {viewing && (
        <DetailModal title="Risk Alert Details" onClose={() => setViewing(null)} testid="risk-details-modal" maxWidth={560}>
          <div className="rounded-[10px] border bg-[#0A0F1F] divide-y px-5" style={{ borderColor: C.border }}>
            <DetailRow label="Alert ID"><span className="font-mono text-[11px]">{viewing.alert_id}</span></DetailRow>
            <DetailRow label="Type">{viewing.type}</DetailRow>
            <DetailRow label="Severity"><Pill value={viewing.severity} /></DetailRow>
            <DetailRow label="Market">{viewing.market}</DetailRow>
            <DetailRow label="Level">{viewing.level}</DetailRow>
            <DetailRow label="Status"><Pill value={viewing.status} /></DetailRow>
            <DetailRow label="Triggered At">{new Date(viewing.created_at).toLocaleString()}</DetailRow>
            {viewing.resolved_at && <DetailRow label="Resolved At">{new Date(viewing.resolved_at).toLocaleString()}</DetailRow>}
            {viewing.resolved_by && <DetailRow label="Resolved By">{viewing.resolved_by}</DetailRow>}
          </div>
          <div className="mt-4 p-4 rounded-[10px] border bg-[#0A0F1F]" style={{ borderColor: C.border }}>
            <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: C.muted }}>Description</div>
            <div className="text-[13px]" style={{ color: C.text }}>{viewing.description}</div>
          </div>
          <div className="flex gap-3 pt-5">
            <button onClick={() => setViewing(null)}
              className="flex-1 h-[44px] rounded-[10px] border font-bold text-[13px]"
              style={{ borderColor: C.border, color: C.text }}>Close</button>
            {viewing.status === "open" && (
              <button onClick={async () => { await resolve(viewing.alert_id); setViewing(null); }}
                data-testid="risk-modal-resolve"
                className="flex-1 h-[44px] rounded-[10px] font-bold text-[13px]"
                style={{ background: C.green, color: "#fff" }}>Mark Resolved</button>
            )}
          </div>
        </DetailModal>
      )}
    </div>
  );
}

function EmergencyBtn({ icon: Icon, color, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-2 h-[44px] px-5 rounded-[10px] font-bold text-[13px] border transition hover:opacity-90"
      style={{ background: `${color}15`, borderColor: `${color}55`, color }}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ============================================================================
// AUDIT LOGS
// ============================================================================
function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [compliance, setCompliance] = useState({});
  const pager = useAdminPager();

  const reload = () => api.get("/api/admin/spot/audit", {
    params: { action, date_from: from || undefined, date_to: to || undefined, skip: pager.skip, limit: pager.limit },
  }).then(({ data }) => {
    const { items, total } = unwrapAdminList(data, "logs");
    setLogs(asAdminList(items).slice(0, pager.pageSize));
    pager.setTotal(total);
  });
  useEffect(() => { pager.resetPage(); }, [action, from, to, pager.resetPage]);
  useEffect(() => { reload(); }, [action, from, to, pager.page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/api/admin/spot/audit/compliance").then(({ data }) => setCompliance(data)); }, []);

  function exportCsv() {
    const params = new URLSearchParams();
    if (action !== "all") params.set("action", action);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    const url = `${apiBaseUrl()}/api/admin/spot/audit/export.csv?${params.toString()}`;
    window.open(url, "_blank");
  }

  return (
    <div className="space-y-5">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SelectBox value={action} onChange={setAction} testid="audit-action-filter" >
            <option value="all">All Actions</option>
            <option value="pair_created">Pair Created</option>
            <option value="pair_halted">Pair Halted</option>
            <option value="order_cancel">Order Cancel</option>
            <option value="orders_bulk_cancel">Bulk Cancel</option>
            <option value="emergency_halt_all_markets">Emergency Halt</option>
            <option value="emergency_cancel_all_orders">Emergency Cancel</option>
            <option value="emergency_disable_new_orders">Emergency Disable</option>
            <option value="liquidity_config_update">Liquidity Update</option>
            <option value="risk_alert_resolve">Risk Resolve</option>
            <option value="fee_update">Fee Update</option>
          </SelectBox>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            data-testid="audit-from"
            className="h-[40px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[13px] outline-none"
            style={{ borderColor: C.border, color: C.text, colorScheme: "dark" }} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            data-testid="audit-to"
            className="h-[40px] px-3 rounded-[10px] border bg-[#0A0F1F] text-[13px] outline-none"
            style={{ borderColor: C.border, color: C.text, colorScheme: "dark" }} />
          <button onClick={exportCsv} data-testid="audit-export"
            className="ml-auto inline-flex items-center gap-1.5 h-[40px] px-4 rounded-[10px] font-bold text-[13px]"
            style={{ background: C.gold, color: C.bg }}>
            <Download size={13} /> Export Logs
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Spot Trading Audit Trail</h3>
          <GhostBtn color={C.gold} onClick={reload}><RefreshCw size={13} /> Refresh</GhostBtn>
        </div>

        {logs.length === 0 ? (
          <div className="p-12 text-center" style={{ color: C.muted }}>No audit logs found</div>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="audit-table">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: C.border, color: C.muted }}>
                  {["Timestamp","Action","Actor","Target","Market","Details"].map((h) => (
                    <th key={h} className="px-5 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: C.border }}>
                {logs.map((l) => (
                  <tr key={l.id} className="hover:bg-[#13192C]">
                    <td className="px-5 py-3 font-mono text-[11px]" style={{ color: C.muted }}>
                      {new Date(l.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 font-bold">{l.action}</td>
                    <td className="px-5 py-3" style={{ color: C.muted }}>{l.actor_email}</td>
                    <td className="px-5 py-3 font-mono text-[11px]">{l.target}</td>
                    <td className="px-5 py-3">{l.market || "—"}</td>
                    <td className="px-5 py-3 font-mono text-[11px] max-w-[260px] truncate" style={{ color: C.muted }}>
                      {JSON.stringify(l.details || {})}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="spot-audit-pager" emptyLabel="No audit logs" flush />
      </Card>

      {/* Compliance access */}
      <Card className="p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Shield size={14} style={{ color: C.green }} /> Compliance Access</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-[12px] border bg-[#0A0F1F]" style={{ borderColor: C.border }}>
            <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: C.muted }}>Regulator Read-Only Access</div>
            <div className="font-bold text-[15px]">
              {compliance.regulator_readonly_enabled ? `Enabled for ${compliance.regulator_label}` : "Disabled"}
            </div>
            {compliance.regulator_last_accessed && (
              <div className="text-[11px] mt-2" style={{ color: C.dim }}>
                Last accessed: {new Date(compliance.regulator_last_accessed).toLocaleString()}
              </div>
            )}
          </div>
          <div className="p-4 rounded-[12px] border bg-[#0A0F1F]" style={{ borderColor: C.border }}>
            <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: C.muted }}>Historical Replay</div>
            <div className="font-bold text-[15px]">Available for last {compliance.historical_replay_days || 365} days</div>
            <div className="text-[11px] mt-2" style={{ color: C.dim }}>
              Storage: {compliance.storage_used_tb || "0"} TB / {compliance.storage_total_tb || "0"} TB
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
